import React, { useState, useMemo } from 'react';
import {
    Sparkles, ChevronDown, ChevronUp, Video, FileText, ChefHat,
    Plane, Mic, ShoppingBag, Map as MapIcon, Newspaper, Brain,
} from 'lucide-react';

/**
 * InspireMeDrawer - a collapsible "Need inspiration?" expander that surfaces
 * 6-8 curated example URLs the user can one-click drop into the Add Bookmark
 * URL input. Helps users past the blank-page freeze AND doubles as a content
 * discovery surface that we can rotate seasonally.
 *
 * Examples are grouped by month-bucket so January users see Oscars-adjacent
 * recipes, December users see holiday cookies, etc. Client-only for now -
 * no backend call needed.
 */

// Month buckets: 0=Jan … 11=Dec
const MONTHLY_EXAMPLES = {
    // January - new year, productivity, health
    0: [
        { category: 'Productivity', icon: Brain, title: 'Productivity habits that actually stick', url: 'https://jamesclear.com/habits' },
        { category: 'Cooking', icon: ChefHat, title: 'Healthy meal-prep ideas', url: 'https://www.bbcgoodfood.com/recipes/collection/healthy-meal-prep-recipes' },
        { category: 'Video', icon: Video, title: 'A TED talk on focus', url: 'https://www.ted.com/talks/johann_hari_this_could_be_why_you_can_t_focus' },
        { category: 'Reading', icon: FileText, title: 'Longreads: Essay of the week', url: 'https://longreads.com/' },
    ],
    // February - Oscars, Valentine's
    1: [
        { category: 'Cooking', icon: ChefHat, title: 'Date-night dinner recipes', url: 'https://www.bonappetit.com/recipe/date-night-steak-dinner' },
        { category: 'Film', icon: Video, title: 'Best Picture nominees', url: 'https://www.oscars.org/' },
        { category: 'Travel', icon: Plane, title: 'Romantic weekend getaways', url: 'https://www.cntraveler.com/story/the-most-romantic-weekend-getaways-in-the-us' },
    ],
    // March - spring, travel planning
    2: [
        { category: 'Travel', icon: Plane, title: 'Spring break destinations', url: 'https://www.nationalgeographic.com/travel/article/best-spring-break-destinations' },
        { category: 'Recipe', icon: ChefHat, title: 'Spring vegetable pasta', url: 'https://www.nytimes.com/recipes/tag/spring' },
    ],
    // April - spring cleaning, Easter
    3: [
        { category: 'Home', icon: Sparkles, title: 'Spring cleaning checklist', url: 'https://www.marthastewart.com/1517796/spring-cleaning-checklist' },
    ],
    // May - summer prep
    4: [
        { category: 'Fitness', icon: Sparkles, title: 'Outdoor workout ideas', url: 'https://www.nike.com/a/outdoor-workouts' },
        { category: 'Travel', icon: Plane, title: 'European summer itineraries', url: 'https://www.lonelyplanet.com/europe' },
    ],
    // June - summer, BBQ
    5: [
        { category: 'Cooking', icon: ChefHat, title: 'BBQ recipes that impress', url: 'https://www.seriouseats.com/grilling-5117857' },
    ],
    // July - summer, vacation
    6: [
        { category: 'Travel', icon: Plane, title: 'Beach reads for the summer', url: 'https://www.nytimes.com/2024/05/22/books/review/summer-reading-recommendations.html' },
    ],
    // August - back-to-school
    7: [
        { category: 'Learning', icon: Brain, title: 'Back-to-school study tips', url: 'https://www.edutopia.org/' },
    ],
    // September - fall, productivity
    8: [
        { category: 'Cooking', icon: ChefHat, title: 'Pumpkin-spice everything', url: 'https://cooking.nytimes.com/topics/pumpkin-recipes' },
        { category: 'Video', icon: Video, title: 'Tech keynote recap', url: 'https://www.youtube.com/results?search_query=apple+keynote' },
    ],
    // October - fall, Halloween
    9: [
        { category: 'Cooking', icon: ChefHat, title: 'Halloween party appetizers', url: 'https://www.foodnetwork.com/topics/halloween' },
        { category: 'Film', icon: Video, title: 'Best horror movies of all time', url: 'https://www.rottentomatoes.com/guide/best-horror-movies-of-all-time/' },
    ],
    // November - Thanksgiving, gift ideas
    10: [
        { category: 'Cooking', icon: ChefHat, title: 'Thanksgiving turkey recipe', url: 'https://cooking.nytimes.com/topics/thanksgiving-recipes' },
        { category: 'Shopping', icon: ShoppingBag, title: 'Black Friday deal watchlist', url: 'https://www.wirecutter.com/blog/black-friday-cyber-monday-deals/' },
    ],
    // December - holidays, wrapped
    11: [
        { category: 'Cooking', icon: ChefHat, title: 'Holiday cookie recipes', url: 'https://www.marthastewart.com/1160064/holiday-cookies' },
        { category: 'Gifts', icon: ShoppingBag, title: 'Wirecutter gift guide', url: 'https://www.wirecutter.com/gifts/' },
        { category: 'Review', icon: Newspaper, title: 'Year-in-review articles', url: 'https://www.newyorker.com/culture/2023-in-review' },
    ],
};

// Evergreen examples that always show alongside the monthly ones
const EVERGREEN_EXAMPLES = [
    { category: 'Video', icon: Video, title: 'A TED talk worth your time', url: 'https://www.ted.com/talks/browse?sort=popular' },
    { category: 'Cooking', icon: ChefHat, title: 'A recipe to save for later', url: 'https://www.seriouseats.com/recipes' },
    { category: 'Travel', icon: MapIcon, title: 'A place you want to visit', url: 'https://www.google.com/maps' },
    { category: 'Podcast', icon: Mic, title: 'A podcast episode to transcribe', url: 'https://open.spotify.com/genre/podcasts-page' },
];

export default function InspireMeDrawer({ onPick, hidden = false }) {
    const [open, setOpen] = useState(false);

    // Build the list: current month's seasonal picks + a few evergreens,
    // capped at 7 so the drawer stays scannable.
    const picks = useMemo(() => {
        const month = new Date().getMonth();
        const seasonal = MONTHLY_EXAMPLES[month] || [];
        const combined = [...seasonal, ...EVERGREEN_EXAMPLES];
        // Dedupe by url in case of overlap
        const seen = new Set();
        return combined.filter(x => {
            if (seen.has(x.url)) return false;
            seen.add(x.url);
            return true;
        }).slice(0, 7);
    }, []);

    // Caller can hide the trigger when the URL bar already has content -
    // the "Not sure what to save?" affordance is only useful before the
    // user has committed to anything.
    if (hidden) return null;

    return (
        <div className="mt-2">
            <button
                type="button"
                onClick={() => setOpen(v => !v)}
                className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-medium transition-colors"
                data-testid="inspire-me-toggle"
            >
                <Sparkles className="w-3 h-3" />
                {open ? 'Hide inspiration' : 'Not sure what to save?'}
                {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>

            {open && (
                <div
                    className="mt-2 rounded-xl border border-border bg-secondary/30 p-2 space-y-1 max-h-60 overflow-y-auto animate-in fade-in-0 slide-in-from-top-1 duration-200"
                    data-testid="inspire-me-list"
                >
                    {picks.map((item) => {
                        const Icon = item.icon;
                        return (
                            <button
                                key={item.url}
                                type="button"
                                onClick={() => { onPick?.(item.url); setOpen(false); }}
                                className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-white transition-colors text-left"
                                data-testid={`inspire-me-item-${item.category.toLowerCase()}`}
                            >
                                <div className="w-7 h-7 rounded-md bg-white border border-border flex items-center justify-center shrink-0">
                                    <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-foreground truncate">{item.title}</p>
                                    <p className="text-[10px] text-muted-foreground truncate">{item.url.replace(/^https?:\/\//, '').split('/')[0]}</p>
                                </div>
                                <span className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground/70 shrink-0">
                                    {item.category}
                                </span>
                            </button>
                        );
                    })}
                    <p className="text-[10px] text-muted-foreground/70 text-center pt-1.5 pb-0.5" data-testid="inspire-me-footer">
                        Tap any row to drop the URL into the input above.
                    </p>
                </div>
            )}
        </div>
    );
}
